import { useEffect, useState } from 'react';
import { useStyles as useStylesButtons } from '../../../../pages/Assessment/RGZButtons.styles';
import DoneIcon from '@mui/icons-material/Done';
import { Prompt, SubText } from '../../../Prompt/Prompt.component';
import Typography from '@material-ui/core/Typography';
import { SnackbarDownload } from '../../../SnackbarDownload/SnackbarDownload.component';
import {
  assignExamTaskGroupSolutionToSampleAssessmentGroup,
  isExamTaskGroupSolutionAssignedToSampleAssessmentGroup,
} from '../../../../services/scoringModel/scoringModel.service';
import { ExamTaskGroupSolutionAssignmentState } from '../../../../services/scoringModel/scoringModel.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { ButtonOne } from '../../../ButtonOne/ButtonOne.component';

interface AssignToSampleAssessmentGroupProps {
  examTaskGroupSolutionId?: string;
  isVisible: boolean;
}

export const AssignToSampleAssessmentGroup: React.FC<AssignToSampleAssessmentGroupProps> = (props) => {
  const internalErrorMessage = {
    mainText: `Wystąpił błąd przy próbie przypisania RGZ: ID_${props.examTaskGroupSolutionId}`,
    subTexts: [{ text: 'Spróbuj ponownie później' }],
  };
  const alreadyAssignedToSomeoneElseErrorMessage = {
    mainText: `Nie można przypisać RGZ: ID_${props.examTaskGroupSolutionId} ponieważ inny CZWO przypisał je do siebie.`,
  };
  const alreadyAssignedToUserErrorMessage = {
    mainText: `Nie można przypisać RGZ: ID_${props.examTaskGroupSolutionId} ponieważ jest już ono do Ciebie przypisane.`,
  };

  const buttonClasses = useStylesButtons();
  const { showLoader } = useLayoutContext();
  const [isPromptOpen, setPromptOpen] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<{ mainText: string; subTexts?: SubText[] }>(internalErrorMessage);
  const [isSnackbarVisible, setSnackbarVisible] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isAssigned, setIsAssigned] = useState<boolean>(false);

  const checkIfExamTaskGroupSolutionCanBeAssigned = () => {
    if (props.examTaskGroupSolutionId) {
      isExamTaskGroupSolutionAssignedToSampleAssessmentGroup(props.examTaskGroupSolutionId).then(
        (res: AxiosResponse<ExamTaskGroupSolutionAssignmentState>) => {
          setIsAssigned(res.data === ExamTaskGroupSolutionAssignmentState.AssignedToCurrentUser);
          setIsActive(res.data === ExamTaskGroupSolutionAssignmentState.Unassigned);
        },
        () => {
          console.error('Error during checking if ExamTaskGroupSolution is assigned to SampleAssessmentGroup');
        },
      );
    }
  };

  useEffect(() => {
    if (props.isVisible) {
      checkIfExamTaskGroupSolutionCanBeAssigned();
    }
  }, [props.examTaskGroupSolutionId]);

  const internalErrorOccured = () => {
    showLoader(false);
    setErrorMessages(internalErrorMessage);
    setPromptOpen(true);
  };

  const alreadyAssignedErrorOccured = (assignedToUser?: boolean) => {
    setErrorMessages(assignedToUser ? alreadyAssignedToUserErrorMessage : alreadyAssignedToSomeoneElseErrorMessage);
    setIsAssigned(false);
    setIsActive(false);
    showLoader(false);
    setPromptOpen(true);
  };

  const assignExamTaskGroupSolution = () => {
    if (props.examTaskGroupSolutionId) {
      if (isActive) {
        showLoader(true, 'Przypisuję RGZ');
        isExamTaskGroupSolutionAssignedToSampleAssessmentGroup(props.examTaskGroupSolutionId).then(
          (res: AxiosResponse<ExamTaskGroupSolutionAssignmentState>) => {
            if (res.data === ExamTaskGroupSolutionAssignmentState.Unassigned && props.examTaskGroupSolutionId) {
              assignExamTaskGroupSolutionToSampleAssessmentGroup(props.examTaskGroupSolutionId).then(
                (res: AxiosResponse<boolean>) => {
                  if (res.data === true) {
                    setIsAssigned(true);
                    setIsActive(false);
                    showLoader(false);
                    setSnackbarVisible(true);
                  } else {
                    alreadyAssignedErrorOccured();
                  }
                },
                () => {
                  internalErrorOccured();
                },
              );
            } else if (res.data === ExamTaskGroupSolutionAssignmentState.AssignedToCurrentUser) {
              alreadyAssignedErrorOccured(true);
            } else if (res.data === ExamTaskGroupSolutionAssignmentState.AssignedToOtherExaminer) {
              alreadyAssignedErrorOccured();
            }
          },
          () => {
            internalErrorOccured();
          },
        );
      }
    } else {
      console.error('examTaskGroupSolutionId not availalbe during assignment to sampleAssessmentGroup');
      internalErrorOccured();
    }
  };

  const hidePrompt = () => {
    setPromptOpen(false);
  };

  const hideSnackbar = () => {
    setSnackbarVisible(false);
  };

  if (props.isVisible)
    return (
      <>
        {isAssigned ? (
          <Typography className={buttonClasses.alreadyAssigned}>
            <DoneIcon />
            RGZ PRZYPISANE
          </Typography>
        ) : (
          <ButtonOne
            color="inherit"
            variant="contained"
            disabled={!isActive}
            className={[
              buttonClasses.assignRGZ,
              isActive ? buttonClasses.activeButton : buttonClasses.disableButton,
            ].join(' ')}
            style={{backgroundColor: isActive ? '#347F42' : '#6F707B' }}
            startIcon={isActive ? <DoneIcon /> : undefined}
            onClick={assignExamTaskGroupSolution}
          >
            PRZYPISZ RGZ
          </ButtonOne>
        )}
        <Prompt
          open={isPromptOpen}
          type="error"
          {...errorMessages}
          mainActionButtonText="powrót do RGZ"
          onConfirm={hidePrompt}
        />
        <SnackbarDownload
          open={isSnackbarVisible}
          onClose={hideSnackbar}
          autoHideDuration={4000}
          text="RGZ przypisane"
        />
      </>
    );
  else return <></>;
};
